'use client'

import { useEffect, useRef } from 'react'

/**
 * Hook to detect when the network comes back online.
 * Useful for refreshing sessions when IP might have changed.
 *
 * @param onOnline - Function to call when network is back online
 *
 * @example
 * ```tsx
 * useNetworkChange(() => {
 *   // Refresh session or validate IP
 *   handleTokenRefresh()
 * })
 * ```
 */
export const useNetworkChange = (
  onOnline: () => void | Promise<void>,
): void => {
  const handlerRef = useRef(onOnline)

  // Update handler ref when it changes
  useEffect(() => {
    handlerRef.current = onOnline
  }, [onOnline])

  useEffect(() => {
    if (typeof window === 'undefined') return

    const handleOnline = () => {
      handlerRef.current()
    }

    window.addEventListener('online', handleOnline)

    return () => {
      window.removeEventListener('online', handleOnline)
    }
  }, [])
}
